import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Unlock, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/fieldops/lib/api";
import { invalidateAppSettings } from "@/fieldops/lib/useAppSettings";

/**
 * TIME-1 §8 — Settings > Integrations > Miter. The push ships OFF and cannot be enabled until two
 * consecutive pay periods reconcile to zero variance and an administrator opens the gate with a
 * reason. Until then the only thing that runs is the dry run, which writes nothing anywhere.
 */
const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 864e5).toISOString().slice(0, 10);

export default function IntegrationsSettingsPage() {
  const [s, setS] = useState(null);
  const [period, setPeriod] = useState({ start: daysAgo(14), end: today() });
  const [rec, setRec] = useState(null);
  const [dry, setDry] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/settings/app").then((r) => setS(r.data)).catch(() => setS({}));
  useEffect(() => { load(); }, []);

  const save = async (patch, ok) => {
    try {
      const { data } = await api.patch("/settings/app", patch);
      setS(data); invalidateAppSettings(); toast.success(ok);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const range = () => ({ period_start: `${period.start}T00:00:00+00:00`,
                         period_end: `${period.end}T23:59:59+00:00` });

  const run = async (what) => {
    setBusy(true);
    try {
      if (what === "rec") {
        const { data } = await api.get("/time/miter/reconciliation", { params: range() });
        setRec(data);
      } else {
        const { data } = await api.get("/time/miter/dry-run", { params: range() });
        setDry(data);
        toast.success(`Dry run built ${data.entries} entr${data.entries === 1 ? "y" : "ies"}`,
                      { description: `${data.hours} h — nothing was sent` });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not run that"); }
    finally { setBusy(false); }
  };

  const recordPeriod = async () => {
    try {
      const { data } = await api.post("/time/miter/gate/record-period",
                                      { ...range(), reason: "parallel run" });
      toast.success("Period recorded as clean",
                    { description: `${data.clean_periods.length} of 2 needed` });
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.detail || "That period is not clean");
    }
  };

  const openGate = async () => {
    const reason = window.prompt("Why is the push being enabled? (logged)");
    if (!reason) return;
    try {
      await api.post("/time/miter/gate/open", { reason });
      toast.success("Parallel-run gate opened"); await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.detail || "The gate stays closed");
    }
  };

  if (!s) return null;
  const clean = s.miter_clean_periods || [];

  return (
    <div className="page-body" data-testid="integrations-settings-page">
      <h1 className="text-xl font-semibold tracking-tight text-slate-900">Integrations</h1>
      <p className="text-sm text-slate-500 mt-1 mb-5">
        Miter is the payroll system of record. This app captures, reconciles and approves time —
        it never calculates pay.
      </p>

      <section className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <label className="text-sm text-slate-700 flex items-start gap-2">
          <input type="checkbox" className="mt-1" data-testid="settings-miter-push-enabled"
                 checked={!!s.miter_push_enabled}
                 onChange={(e) => save({ miter_push_enabled: e.target.checked },
                                        e.target.checked ? "Push switch on" : "Push switch off")} />
          <span>
            <b>Push approved time to Miter</b>
            <span className="block text-xs text-slate-500 mt-0.5">
              Even with this on, nothing is sent until the parallel-run gate below is open. Miter's
              API is private — request API access or the file-import format from Miter and the
              adapter behind this one interface sends the same payload either way.
            </span>
          </span>
        </label>
        <div className="mt-3 flex items-center gap-2">
          <span className="text-xs text-slate-500">Delivery</span>
          {[["file", "File import"], ["api", "API"]].map(([v, label]) => (
            <button key={v} data-testid={`settings-miter-mode-${v}`}
                    onClick={() => save({ miter_mode: v }, `Delivery: ${label}`)}
                    className={`h-8 px-3 text-sm rounded-md border ${
                      (s.miter_mode || "file") === v ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white border-slate-300 text-slate-700"}`}>
              {label}
            </button>
          ))}
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          {s.miter_gate_open ? <Unlock className="h-4 w-4 text-emerald-600" />
                             : <Lock className="h-4 w-4 text-amber-600" />}
          Parallel-run gate — {s.miter_gate_open ? "open" : "closed"}
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">
          Two consecutive pay periods must reconcile to zero variance before the push can be
          enabled. {clean.length} of 2 recorded{clean.length ? `: ${clean.join(", ")}` : ""}.
        </p>
        <div className="mt-3 flex items-end gap-2 flex-wrap">
          <label className="text-xs text-slate-500">Period start
            <Input type="date" value={period.start} data-testid="settings-miter-period-start"
                   onChange={(e) => setPeriod((p) => ({ ...p, start: e.target.value }))}
                   className="h-9 text-sm mt-0.5" />
          </label>
          <label className="text-xs text-slate-500">Period end
            <Input type="date" value={period.end} data-testid="settings-miter-period-end"
                   onChange={(e) => setPeriod((p) => ({ ...p, end: e.target.value }))}
                   className="h-9 text-sm mt-0.5" />
          </label>
          <Button size="sm" variant="outline" className="h-9" disabled={busy}
                  onClick={() => run("rec")} data-testid="settings-miter-reconcile">
            Reconciliation report
          </Button>
          <Button size="sm" variant="outline" className="h-9" disabled={busy}
                  onClick={() => run("dry")} data-testid="settings-miter-dry-run">
            <Play className="h-3.5 w-3.5 mr-1" /> Dry run
          </Button>
          <Button size="sm" variant="outline" className="h-9" disabled={busy}
                  onClick={recordPeriod} data-testid="settings-miter-record-period">
            Record this period as clean
          </Button>
          {!s.miter_gate_open && (
            <Button size="sm" className="h-9" disabled={clean.length < 2} onClick={openGate}
                    data-testid="settings-miter-open-gate">Open the gate</Button>
          )}
        </div>

        {rec && (
          <div className="mt-3 rounded-md border border-slate-200 overflow-hidden" data-testid="miter-reconciliation">
            <div className="px-2 py-1.5 bg-slate-50 text-xs flex items-center gap-2">
              <b>App {rec.app_hours} h</b> vs <b>Miter {rec.miter_hours} h</b>
              <span className={rec.zero_variance ? "text-emerald-700 font-semibold"
                                                 : "text-red-700 font-semibold"}>
                variance {rec.total_variance} h
              </span>
              {!rec.miter_hours_loaded && (
                <span className="text-slate-500">— no Miter hours loaded for this period yet</span>
              )}
            </div>
            <table className="w-full text-xs">
              <thead className="bg-white text-slate-500">
                <tr><th className="text-left px-2 py-1">Person</th><th className="text-left px-2 py-1">Date</th>
                  <th className="text-right px-2 py-1">App</th><th className="text-right px-2 py-1">Miter</th>
                  <th className="text-right px-2 py-1">Variance</th></tr>
              </thead>
              <tbody>
                {rec.rows.slice(0, 40).map((r) => (
                  <tr key={`${r.user_email}${r.date}`} className="border-t border-slate-100"
                      data-testid={`miter-rec-row-${r.date}`}>
                    <td className="px-2 py-1">{r.user_email}</td>
                    <td className="px-2 py-1">{r.date}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.app_hours}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{r.miter_hours}</td>
                    <td className={`px-2 py-1 text-right tabular-nums ${
                      r.variance ? "text-red-700 font-semibold" : "text-slate-400"}`}>{r.variance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {dry && (
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="miter-dry-run">
            <p className="text-xs text-slate-700">
              <b>{dry.entries} approved entr{dry.entries === 1 ? "y" : "ies"} · {dry.hours} h</b> —
              {" "}{dry.writes} writes. Mode: {dry.mode}.
            </p>
            <pre className="mt-1 max-h-48 overflow-auto text-[10px] text-slate-600 whitespace-pre">
              {dry.csv}
            </pre>
          </div>
        )}
      </section>
    </div>
  );
}
